import { useContext, useEffect, useState } from 'react';
import { Col, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { CampoFormik } from '../../../components/CompUtils';
import es from '../../../yup-es';
import { cvTiposSelector } from '../cvSlice';
import { SeccionContext } from '../itemTypes';
import FormikContainer from './FormikContainer';

yup.setLocale(es);

//
// ─── SCHEMA LABORALES ────────────────────────────────────────────────
//

const LaboralesSchema = yup
  .object({
    idAntecedenteLaboralCv: yup.number(), //si existe, es edicion
    cargo: yup.string().required(),
    idTipoAntecedenteLaboral: yup.number().required(),
    idPais: yup.number().required(),
    lugarTrabajo: yup.string().required(),
    descripcionTarea: yup.string().required(),
    tienePersonalACargo: yup.boolean(),
    personalACargo: yup
      .number()
      .nullable()
      .transform((curr, orig) => (('' + orig).trim() === '' ? null : curr))
      .when('tienePersonalACargo', {
        is: true,
        then: yup.number().required(),
      }),
  })
  .shape(
    {
      actualidad: yup.boolean(),
      f_desde: yup
        .date()
        .required('La fecha desde es requerida')
        .test('valid-date', 'Fecha desde inválida', (value) => {
          if (value) {
            const year = value.getFullYear().toString();
            const month = (value.getMonth() + 1).toString().padStart(2, '0');
            const day = value.getDate().toString().padStart(2, '0');
            return (
              year >= '1000' &&
              year <= '9999' &&
              month >= '01' &&
              month <= '12' &&
              day >= '01' &&
              day <= '31'
            );
          }
          return true;
        }),
      f_hasta: yup
        .date()
        .nullable()
        .transform((curr, orig) => (orig === '' ? null : curr))
        .when('actualidad', {
          is: false,
          then: yup.date().required('La fecha hasta es requerida'),
        })
        .when(
          'f_desde',
          (ingreso, schema) =>
            ingreso &&
            yup
              .date()
              .test('valid-date', 'Fecha hasta inválida', (value) => {
                if (value) {
                  const year = value.getFullYear().toString();
                  const month = (value.getMonth() + 1).toString().padStart(2, '0');
                  const day = value.getDate().toString().padStart(2, '0');
                  return (
                    year >= '1000' &&
                    year <= '9999' &&
                    month >= '01' &&
                    month <= '12' &&
                    day >= '01' &&
                    day <= '31'
                  );
                }
                return true;
              })
              .min(yup.ref('f_desde'), 'Rango de fechas incorrecto'),
        ),
    },
    [('f_desde', 'f_hasta', 'actualidad')],
  );

//
// ─── FORMULARIO LABORALES ──────────────────────────────────────────────────
//

export const FormLaborales = () => {
  const formState = useContext(SeccionContext);
  const tiposLaborales = useSelector(cvTiposSelector).tiposLaborales;

  const [hastaDisabled, setHastaDisabled] = useState(false);
  const [showPersonalACargo, setShowPersonalACargo] = useState(false);

  useEffect(() => {
    setHastaDisabled(formState.initialValues.actualidad && !formState.initialValues.f_hasta);
    setShowPersonalACargo(formState.initialValues.tienePersonalACargo);
  }, [formState.initialValues]);

  return (
    <FormikContainer
      schema={LaboralesSchema}
      seccionKey="antecedentesLaborales"
      seccionTitle="Antecedentes Laborales">
      {(formik) => (
        <>
          <Form.Row>
            <CampoFormik
              aria-required
              label="Denominación de puesto o Cargo (obligatorio)"
              name="cargo"
              formik={formik}
            />
          </Form.Row>
          <Form.Row>
            <CampoFormik
              aria-required
              label="Denominación del Empleador o Lugar (obligatorio)"
              name="lugarTrabajo"
              formik={formik}
            />
          </Form.Row>
          <Form.Row>
            <Form.Group as={Col} sm={5}>
              <Form.Label as="div" for="TienePersonalACargo">
                <Form.Check
                  id="TienePersonalACargo"
                  type="checkbox"
                  label="Con personal a cargo"
                  name="tienePersonalACargo"
                  checked={formik.values.tienePersonalACargo}
                  onBlur={formik.handleBlur}
                  onChange={(ev) => {
                    formik.setFieldValue('tienePersonalACargo', !formik.values.tienePersonalACargo);
                    if (ev.target.checked) {
                      setShowPersonalACargo(true);
                    } else {
                      formik.setFieldValue('personalACargo', '');
                      setShowPersonalACargo(false);
                    }
                    formik.validateField('personalACargo'); //Para validar required o no segun el caso
                  }}
                />
              </Form.Label>
            </Form.Group>
            <fieldset
              style={{ display: showPersonalACargo ? 'contents' : 'none' }}
              disabled={!showPersonalACargo}>
              <CampoFormik
                aria-required
                style={{ width: 65 }}
                label="Cantidad de personal a cargo (obligatorio)"
                name="personalACargo"
                col={{ sm: 6 }}
                formik={formik}
              />
            </fieldset>
          </Form.Row>
          <Form.Row>
            <CampoFormik
              aria-required
              type="pais"
              label="País (obligatorio)"
              name="idPais"
              formik={formik}
            />
          </Form.Row>
          <Form.Row>
            <CampoFormik
              aria-required
              type="textarea"
              rows={2}
              formik={formik}
              label="Descripción de tareas y responsabilidades (obligatorio)"
              name="descripcionTarea"
              help="Deberá ingresar un breve detalle de la responsabilidad primaria del cargo y al menos 5 tareas específicas realizadas. 
                    Recuerde adjuntar la documentación que certifique dichas tareas y la relación vincular."
            />
          </Form.Row>
          <Form.Row>
            <Form.Group as={Col}>
              <Form.Label for="tipoAntecedente">Sector (obligatorio):</Form.Label>
              <Form.Control
                aria-required
                id="tipoAntecedente"
                as="select"
                name="idTipoAntecedenteLaboral"
                value={formik.values.idTipoAntecedenteLaboral}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  formik.touched.idTipoAntecedenteLaboral &&
                  !!formik.errors.idTipoAntecedenteLaboral
                }>
                <option value="">Seleccione</option>
                {Object.keys(tiposLaborales).map((key) => (
                  <option key={tiposLaborales[key].id} value={tiposLaborales[key].id}>
                    {tiposLaborales[key].descripcion}
                  </option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {formik.errors.idTipoAntecedenteLaboral}
              </Form.Control.Feedback>
            </Form.Group>
          </Form.Row>
          <Form.Row>
            <CampoFormik
              aria-required
              label="Desde (obligatorio)"
              type="date2"
              name="f_desde"
              maxDate={formik.values.f_hasta}
              col={{ sm: 4 }}
              formik={formik}
            />
            <fieldset style={{ display: 'contents' }} disabled={hastaDisabled}>
              <CampoFormik
                aria-required
                label="Hasta (obligatorio)"
                name="f_hasta"
                type="date2"
                minDate={formik.values.f_desde}
                col={{ sm: 4 }}
                formik={formik}
              />
            </fieldset>
            <Form.Group>
              <Form.Label for="actualidad">&nbsp;</Form.Label>
              <Form.Check
                id="actualidad"
                style={{ transform: 'translateY(25%)' }}
                type="checkbox"
                label="Actualidad"
                name="actualidad"
                checked={formik.values.actualidad}
                onBlur={formik.handleBlur}
                onChange={(ev) => {
                  formik.setFieldValue('actualidad', !formik.values.actualidad);
                  if (ev.target.checked) {
                    formik.setFieldValue('f_hasta', '');
                    setHastaDisabled(true);
                  } else {
                    setHastaDisabled(false);
                    formik.validateField('f_hasta');
                  }
                  formik.validateField('f_hasta'); //Para validar required o no segun el caso
                }}
              />
            </Form.Group>
          </Form.Row>
        </>
      )}
    </FormikContainer>
  );
};
